<template>
  <div class="fts-pagination">
    <div class="pagination-summary">
      Showing
      <span class="pagination-summary__value" v-if="shouldShowControls">{{ currentItemIndex }}</span>
      <span class="pagination-summary__value" v-else>{{ totalItems }}</span>
      <template v-if="shouldShowControls">
        of <span class="pagination-summary__value">{{ summaryTotalItems }}</span>
      </template>
      {{ recordLabel }}
      <span class="pagination-summary__note" v-if="filtered"> · filtered</span>
      <span class="pagination-summary__note" v-if="isTotalItemsExceed"> · first {{ getTotalItemsLimit.toLocaleString() }} shown</span>
    </div>

    <div class="page-control-container" v-if="shouldShowControls && isShowPagination">
      <span 
        class="material-icons-outlined page-num-option page-num-option--icon" 
        :class="{'page-num-option__deactivate': isFirstPage}" 
        @click="firstPage"
      >keyboard_double_arrow_left</span>

      <span 
        class="material-icons-outlined page-num-option page-num-option--icon" 
        :class="{'page-num-option__deactivate': isFirstPage}" 
        @click="prevPage"
      >keyboard_arrow_left</span>

      <div 
        v-for="num in pageNumbers.firstPages" 
        :key="`page-${num}`" 
        class="page-num-option"  
        :class="{'page-num-option__active': num === currentPage}" 
        @click="setCurrentPage(num)"
      >
        {{ num + 1 }}
      </div>

      <fts-dropdown-select 
        append-toggle-class="page-num-option" 
        placement="top-end" 
        :options="pageNumbers.middlePages"
        @selected="x => setCurrentPage(x)" v-if="pageNumbers.middlePages.length"
      >
        <template #toggle>
          <span class="material-icons-outlined">more_horiz</span>
        </template>

        <template #option="{ option, select }">
          <div class="page-num-option" @click="select(option)">{{ option + 1 }}</div>
        </template>
      </fts-dropdown-select>

      <template v-if="pageNumbers.lastPages.length">
        <div 
          v-for="num in pageNumbers.lastPages" 
          :key="`page-${num}`" 
          class="page-num-option" 
          :class="{'page-num-option__active': num === currentPage}" 
          @click="setCurrentPage(num)"
        >
          {{ num + 1 }}
        </div>
      </template>

      <span 
        class="material-icons-outlined page-num-option page-num-option--icon" 
        :class="{'page-num-option__deactivate': isLastPage}" 
        @click="nextPage"
      >keyboard_arrow_right</span>

      <span 
        class="material-icons-outlined page-num-option page-num-option--icon" 
        :class="{'page-num-option__deactivate': isLastPage}" 
        @click="lastPage"
      >keyboard_double_arrow_right</span>
    </div>

    <div class="toggle-container" v-if="shouldShowControls">
      <span>Row per page:</span>

      <fts-dropdown-select 
        append-toggle-class="pagination-dropdown-toggle" 
        placement="top-end" 
        :options="pageList"
        v-model="currentPageSize" 
        v-if="pagination"
      >
        <template #toggle>
          {{ currentPageSize }}
          <font-awesome-icon :icon="['fas', 'chevron-down']" class="pagination-dropdown-toggle-icon" />
        </template>
      </fts-dropdown-select>
    </div>
  </div>
</template>

<script>
import commons from '../commons/commons';
import { 
  Listing_Max_Total_Entries,
  Lite_Listing_Pagination_Total_Items_Threshold
} from '../commons/flags';
import FTSDropdownSelect from "../elements/FTSDropdownSelect";

export default {
  props: {
    pagination: {
      type: Boolean,
      default: true,
    },
    totalItems: {
      type: Number,
      default: 0
    },
    totalItemsLimit: Number,
    // calm density P1b: the status strip says when the count is a filtered one
    filtered: Boolean,
    pageSize: {
      type: Number,
      default: 200
    },
    controlsTotalItemsThreshold: {
      type: Number,
      default: Lite_Listing_Pagination_Total_Items_Threshold
    },
    limit: Number
  },
  model: {
    prop: "pageSize",
    event: "updatePageSize",
  },
  data: () => ({
    currentPage: 0,
    pageList: [10, 20, 50, 100, 200],
  }),
  components: {
    "fts-dropdown-select": FTSDropdownSelect,
  },
  methods: {
    setCurrentPage(page) {
      this.currentPage = page;
    },
    firstPage() {
      if (this.isFirstPage) return;

      this.currentPage = 0;
    },
    prevPage() {
      if (this.isFirstPage) return;
      this.currentPage = this.currentPage - 1;
    },
    nextPage() {
      if (this.isLastPage) return;
      this.currentPage = this.currentPage + 1;
    },
    lastPage() {
      if (this.isLastPage) return;
      this.currentPage = Math.ceil(this.totalItems / this.pageSize) - 1;
    },
  },
  watch: {
    currentPage: function () {
      this.$emit('pageChanged', this.currentPage);
    },
    pageSize: function () {
      if ((this.currentPage * this.pageSize) + 1 > this.totalItems) {
        this.currentPage = Math.max(Math.ceil(this.totalItems / this.pageSize) - 1, 0);
      } else {
        this.$emit('pageChanged', this.currentPage);
      }
    },
    totalItems: function () {
      this.currentPage = 0;
    }
  },
  computed: {
    lastPageNumber() {
      return Math.ceil(this.totalItems / this.pageSize) - 1;
    },
    currentItemIndex() {
      const startItemIndex = (this.currentPage * this.pageSize) + 1;
      const endItemIndex = Math.min((this.currentPage + 1) * this.pageSize, this.totalItems);
      return `${startItemIndex} - ${endItemIndex}`;
    },
    pageNumbers() {
      const total = Math.ceil(this.totalItems / this.pageSize) // total pages
      const current = this.currentPage

      let firstPages = []
      let middlePages = []
      let lastPages = []

      if (total <= 0) {
        return { firstPages, middlePages, lastPages }
      }

      if (total <= 4) {
        firstPages = Array.from(
          new Set(
            [0, 1, 2, 3].filter(p => p < total)
          )
        )
      } else if (current >= total - 3) {
        firstPages = [0].filter(p => p >= 0);
        lastPages = [total - 4, total - 3, total - 2, total - 1]

        middlePages = []

        for (let i = firstPages[0] + 1; i < lastPages[0]; i++) {
          middlePages.push(i)
        }
      } else {
        firstPages = [current - 1, current, current + 1, current + 2].filter(p => p >= 0).slice(0, 3);

        lastPages = [total - 1];

        middlePages = []

        for (let i = firstPages[0] + 1; i < lastPages[0]; i++) {
          middlePages.push(i)
        }
      }
      return {
        firstPages,
        middlePages,
        lastPages
      }
    },
    currentPageSize: {
      get() {
        return this.pageSize;
      },
      set(val) {
        this.$emit('updatePageSize', val);
        this.$emit('page-size-changed', val);
      },
    },
    isFirstPage() {
      return this.currentPage === 0;
    },
    isLastPage() {
      if (this.totalItems == 0) return true;
      return Boolean(this.currentPage === Math.ceil(this.totalItems / this.pageSize) - 1);
    },
    getTotalItemsLimit() {
      if (this.totalItemsLimit && commons.isInteger(this.totalItemsLimit) && this.totalItemsLimit > 0) {
        return this.totalItemsLimit;
      }

      return Listing_Max_Total_Entries;
    },
    isTotalItemsExceed() {
      return this.totalItems >= this.getTotalItemsLimit;
    },
    summaryTotalItems() {
      return this.isTotalItemsExceed ? `${this.getTotalItemsLimit - 1}+` : this.totalItems;
    },
    recordLabel() {
      return this.totalItems === 1 ? 'record' : 'records';
    },
    shouldShowControls() {
      if (!commons.isInteger(this.controlsTotalItemsThreshold)) {
        return true;
      }

      return this.totalItems > this.controlsTotalItemsThreshold;
    },
    isShowPagination() {
      return this.totalItems > this.pageSize;
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  color: var(--inistate-text-primary);
  gap: 16px;

  .pagination-summary {
    margin-right: auto;
  }

  .pagination-summary__value {
    font-weight: var(--inistate-font-weight-medium);
  }

  .page-control-container {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .page-num-option {
    display: flex;
    width: 36px;
    min-height: 36px;
    align-items: center;
    justify-content: center;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    cursor: pointer;

    &--icon {
      color: var(--inistate-icon-primary);
    }

    &:not(.page-num-option__active, .page-num-option__deactivate) {
      &:hover {
        background-color: var(--inistate-color-neutral-50);
      }
    }

    &__active {
      background-color: var(--inistate-color-neutral-100);
      font-weight: var(--inistate-font-weight-bold);
    }

    &__deactivate {
      color: var(--inistate-icon-secondary);
      cursor: not-allowed;
    }
  }

  .toggle-container {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .pagination-dropdown-toggle {
    padding: 7px 12px;
    background-color: var(--inistate-color-shade-white);
    border: var(--inistate-color-neutral-100) 1px solid;
    border-radius: var(--inistate-rounded-4);

    .pagination-dropdown-toggle-icon {
      margin-left: 6px;
    }
  }
}
</style>
